import React, { useEffect, useRef, useState } from 'react'
import { Wrapper } from '@googlemaps/react-wrapper'
import Badge from '@mui/material/Badge'
import { ChevronRightRounded, NotificationsOutlined } from '@mui/icons-material'
import {
  MapGoogle,
  MAP_LIBRARIES,
  MapSelector,
  MenuMap,
  NotificationsPanel,
  NotificationsPrompt,
  RecaptchaNotice,
} from '../../components'
import { AlertDialog } from '../../components/AlertDialog'
import {
  describePlan,
  detectInAppBrowser,
  locationSettingsHint,
  supportWhatsAppUrl,
} from '../../utils'
import { Constants } from '../../theme'
import {
  MapRoot,
  BellButton,
  PlanButton,
  MapSelectorContainer,
  FooterContainer,
  TextParragraph,
  HeaderCard,
  Title,
} from './styles'

const { MAP_STREET_ZOOM: STREET_ZOOM, MAP_BUILDINGS_TILT: BUILDINGS_TILT } = Constants

// Invitación a activar notificaciones al entrar: si toca "Ahora no" no se repite en 3 días.
const PROMPT_DELAY_MS = 1500
const PROMPT_SNOOZE_MS = 3 * 24 * 60 * 60 * 1000
const PROMPT_DISMISSED_KEY = 'trakku.notificationsPromptDismissedAt'

const promptSnoozed = () => {
  try {
    const dismissedAt = Number(window.localStorage.getItem(PROMPT_DISMISSED_KEY))
    return Boolean(dismissedAt) && Date.now() - dismissedAt < PROMPT_SNOOZE_MS
  } catch (error) {
    return false
  }
}

const snoozePrompt = () => {
  try {
    window.localStorage.setItem(PROMPT_DISMISSED_KEY, String(Date.now()))
  } catch (error) {
    // Sin almacenamiento se volverá a preguntar en la próxima visita.
  }
}

function MapPage({
  view,
  setView,
  vehicles,
  position,
  urlShare,
  userLogout,
  handleChange,
  sharePosition,
  selectedVehicle,
  selectedVehicleData,
  userLocation,
  showUserLocation,
  locationError,
  plan,
  notificationPermission,
  enableNotifications,
  notificationHistory,
  unreadCount,
  notificationsReadAt,
  refreshHistory,
  markAsRead,
}) {
  const focusedVehicle = useRef(null)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [promptOpen, setPromptOpen] = useState(false)

  // Solo si aún no decidió: con permiso dado, bloqueado o sin soporte no se muestra.
  useEffect(() => {
    if (notificationPermission !== 'default' || promptSnoozed()) return undefined
    const timeoutId = setTimeout(() => setPromptOpen(true), PROMPT_DELAY_MS)
    return () => clearTimeout(timeoutId)
  }, [notificationPermission])

  const activateFromPrompt = () => {
    setPromptOpen(false)
    enableNotifications()
  }

  const dismissPrompt = () => {
    snoozePrompt()
    setPromptOpen(false)
  }
  const centeredOnUser = useRef(false)
  const [locationHelpClosed, setLocationHelpClosed] = useState(false)
  // Dentro de otra app (Discord, WhatsApp…) la salida es abrir Chrome: ese aviso manda.
  const [inAppBrowser] = useState(() => Boolean(detectInAppBrowser()))
  const [planOpen, setPlanOpen] = useState(false)
  const planInfo = describePlan(plan)
  const planNeedsRenewal = Boolean(planInfo) && planInfo.tone !== 'ok'

  const asSentence = (text) => (/[.!?]$/.test(text.trim()) ? text.trim() : `${text.trim()}.`)

  const planDetail = () => {
    if (!planInfo) return ''
    const parts = [
      `Activación: ${planInfo.activationDate || '--'}.`,
      `Vencimiento: ${planInfo.dueDate || '--'}.`,
    ]
    if (planNeedsRenewal && plan.message) parts.push(asSentence(plan.message))
    if (planNeedsRenewal && plan.description) parts.push(asSentence(plan.description))
    if (planNeedsRenewal) parts.push('Escríbenos por WhatsApp y te ayudamos a renovarlo.')
    return parts.join(' ')
  }

  // La web no tiene pagos: la renovación se hace con soporte por WhatsApp.
  const renewPlan = () => {
    const message = `Hola, quiero renovar el plan de Trakku de mi moto ${selectedVehicle} (chasis ${plan?.chasis}).`
    window.open(supportWhatsAppUrl(message), '_blank', 'noopener,noreferrer')
    setPlanOpen(false)
  }

  // Se guarda hasta cuándo había leído para resaltar lo nuevo mientras el panel está abierto.
  const [newSince, setNewSince] = useState(0)

  const openNotifications = () => {
    setNewSince(notificationsReadAt)
    setNotificationsOpen(true)
    markAsRead()
    refreshHistory()
  }

  const closeNotifications = () => {
    setNotificationsOpen(false)
    markAsRead()
  }

  const center = () => {
    if (selectedVehicleData && selectedVehicleData.length > 0) {
      // Se recuerda qué moto está enfocada (no sus coordenadas): al cambiar de moto
      // se enfoca de inmediato aunque esté quieta y no mande GPS nuevo.
      // Después el mapa la sigue con la animación del marcador (MapGoogle).
      if (focusedVehicle.current === selectedVehicle) return
      focusedVehicle.current = selectedVehicle
      setView({
        detail: false,
        clinic: null,
        center: {
          lat: selectedVehicleData[0].latitude,
          lng: selectedVehicleData[0].longitude,
        },
        zoomLevel: STREET_ZOOM,
        tilt: BUILDINGS_TILT,
      })
    } else if (showUserLocation) {
      // Moto sin GPS: se centra una vez en el usuario para no pelear con su zoom.
      if (!userLocation || centeredOnUser.current) return
      centeredOnUser.current = true
      setView({
        detail: false,
        clinic: null,
        center: {
          lat: userLocation.latitude,
          lng: userLocation.longitude,
        },
        zoomLevel: STREET_ZOOM,
        tilt: BUILDINGS_TILT,
        follow: 'user',
      })
    } else {
      setView({
        detail: false,
        clinic: null,
        center: {
          lat: 4.3641759,
          lng: -73.3833968,
        },
        zoomLevel: 6.5,
        tilt: 0,
      })
    }
  }

  // Va antes del enfoque: los efectos corren en orden y la limpieza debe ocurrir primero.
  useEffect(() => {
    // Al cambiar de moto siempre se enfoca la nueva, aunque antes se haya enfocado al usuario.
    focusedVehicle.current = null
    centeredOnUser.current = false
    setLocationHelpClosed(false)
  }, [selectedVehicle])

  useEffect(() => {
    center()
  }, [selectedVehicleData, selectedVehicle, showUserLocation, userLocation])

  // Botón para volver a la moto (o al usuario si la moto no tiene GPS) después de mover el mapa.
  const recenterTarget = () => {
    if (selectedVehicleData && selectedVehicleData.length > 0) {
      return { lat: selectedVehicleData[0].latitude, lng: selectedVehicleData[0].longitude }
    }
    if (showUserLocation && userLocation) {
      return { lat: userLocation.latitude, lng: userLocation.longitude }
    }
    return null
  }
  const target = recenterTarget()
  const recenter = () => {
    setView({
      detail: false,
      clinic: null,
      center: target,
      zoomLevel: STREET_ZOOM,
      tilt: BUILDINGS_TILT,
      follow: showUserLocation ? 'user' : 'vehicle',
    })
  }

  const footerText = () => {
    if (!showUserLocation) return selectedVehicle
    if (locationError) return `${selectedVehicle} · Sin GPS · Activa tu ubicación`
    return `${selectedVehicle} · Sin GPS`
  }

  return (
    <MapRoot>
      <AlertDialog
        mainbuttonTitle="Entendido"
        selectedVehicleData={{}}
        openAlert={Boolean(locationError) && !locationHelpClosed && !inAppBrowser}
        handleAceptAlert={() => setLocationHelpClosed(true)}
        handleCloseAlert={() => setLocationHelpClosed(true)}
        title="Activa tu ubicación"
        subtitle={`Esta moto no tiene GPS, así que te mostramos dónde estás tú, pero no pudimos obtener tu ubicación. ${locationSettingsHint()}`}
      />
      <AlertDialog
        title={`Plan de datos · ${selectedVehicle}`}
        subtitle={planDetail()}
        mainbuttonTitle="Entendido"
        secondarybuttonTitle={planNeedsRenewal ? 'Renovar' : null}
        selectedVehicleData={{}}
        openAlert={planOpen && Boolean(planInfo)}
        handleAceptAlert={() => setPlanOpen(false)}
        handleCloseAlert={() => setPlanOpen(false)}
        handleCancelAlert={renewPlan}
      />
      <HeaderCard>
        <Title>TRAKKU</Title>
        <BellButton aria-label="Ver notificaciones" onClick={openNotifications}>
          <Badge color="error" badgeContent={unreadCount}>
            <NotificationsOutlined />
          </Badge>
        </BellButton>
      </HeaderCard>
      <NotificationsPrompt
        open={promptOpen && notificationPermission === 'default' && !inAppBrowser}
        onActivate={activateFromPrompt}
        onDismiss={dismissPrompt}
      />
      <NotificationsPanel
        open={notificationsOpen}
        onClose={closeNotifications}
        history={notificationHistory}
        newSince={newSince}
        notificationPermission={notificationPermission}
        onEnableNotifications={enableNotifications}
      />
      <MapSelectorContainer>
        <MapSelector
          vehicles={vehicles}
          handleChange={handleChange}
          selectedVehicle={selectedVehicle}
        />
      </MapSelectorContainer>
      <Wrapper
        apiKey="AIzaSyAINQfRs6ACIur8xjLP8pCIXrZFIoJh39o"
        version="weekly"
        libraries={MAP_LIBRARIES}
      >
        <MapGoogle
          view={view}
          vehicles={position}
          selectedVehicleData={selectedVehicleData}
          userLocation={userLocation}
          showLocationAlert={false}
          onRecenter={target ? recenter : null}
          recenterLabel={showUserLocation ? 'Centrar mi ubicación' : 'Centrar la moto'}
          recenterImage={showUserLocation ? null : selectedVehicleData?.[0]?.img}
          followVehicleKey={showUserLocation ? null : selectedVehicleData?.[0]?.chasis}
        />
      </Wrapper>
      <MenuMap
        urlShare={urlShare}
        selectedVehicleData={selectedVehicleData}
        sharePosition={sharePosition}
        userLogout={userLogout}
        notificationPermission={notificationPermission}
        enableNotifications={enableNotifications}
      />
      <FooterContainer>
        <TextParragraph>{footerText()}</TextParragraph>
        {planInfo ? (
          <PlanButton tone={planInfo.tone} onClick={() => setPlanOpen(true)}>
            {planInfo.summary}
            <ChevronRightRounded fontSize="small" />
          </PlanButton>
        ) : null}
        <RecaptchaNotice compact />
      </FooterContainer>
    </MapRoot>
  )
}

export default MapPage
